import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useAuthContext } from '../../hooks/useAuthContext';
import { userApiClient } from '../../lib/api/client';
import { InfoPanel } from '../InfoPanel';
import { FormField } from '../form/FormField';
import { toast } from 'sonner';

export interface ProfileInfoPanelProps {
  /** Callback after successful save */
  onUpdate?: () => void;
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  displayName: string;
  jobTitle: string;
  phone: string;
  timezone: string;
}

interface ProfileResponse {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  phone?: string;
  timezone?: string;
  email: string;
}

/**
 * ProfileInfoPanel - Domain-specific panel for user profile information
 *
 * Displays and edits basic profile info (name, job title, phone, timezone).
 * Uses InfoPanel wrapper for consistent UI. Manages own data fetching and mutations.
 *
 * Pattern follows OrganizationInfoPanel architecture.
 *
 * @example
 * // In settings/profile modal
 * <ProfileInfoPanel onUpdate={() => console.log('saved')} />
 */
export function ProfileInfoPanel({ onUpdate }: ProfileInfoPanelProps) {
  const { refreshUser } = useAuthContext();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData | null>(null);

  // Common timezones for quick selection
  const commonTimezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
  ];

  // Fetch profile data
  const {
    data: profile,
    isLoading,
    error,
  } = useQuery<ProfileResponse>({
    queryKey: ['profile'],
    queryFn: () => userApiClient.get('/api/v1/users/me'),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ProfileFormData) =>
      userApiClient.put('/api/v1/users/me', {
        firstName: data.firstName,
        lastName: data.lastName,
        displayName: data.displayName.trim() || null,
        jobTitle: data.jobTitle || null,
        phone: data.phone || null,
        timezone: data.timezone,
      }),
    onSuccess: async () => {
      setIsEditing(false);
      setFormData(null);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      await refreshUser();
      onUpdate?.();
      toast.success('Profile updated');
    },
    onError: () => {
      toast.error('Failed to update profile');
    },
  });

  const handleEdit = () => {
    if (!profile) return;
    setFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      displayName: profile.displayName || '',
      jobTitle: profile.jobTitle || '',
      phone: profile.phone || '',
      timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!formData) return;

    // Client-side validation
    if (formData.firstName.trim().length < 1) {
      toast.error('First name is required');
      return;
    }
    if (formData.lastName.trim().length < 1) {
      toast.error('Last name is required');
      return;
    }

    updateMutation.mutate(formData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(null);
  };

  if (isLoading) {
    return (
      <InfoPanel title="Basic Information">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      </InfoPanel>
    );
  }

  if (error) {
    return (
      <InfoPanel title="Basic Information">
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <p className="text-sm text-red-800 dark:text-red-200">Failed to load profile</p>
        </div>
      </InfoPanel>
    );
  }

  if (!profile) return null;

  return (
    <InfoPanel
      title="Basic Information"
      subtitle="Manage your name, contact information, and timezone"
      isEditable={true}
      isEditing={isEditing}
      onEditToggle={handleEdit}
      actions={
        isEditing ? (
          <>
            <button
              type="button"
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-400 dark:disabled:bg-zinc-600 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center gap-2"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                'Save Changes'
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-300 font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </>
        ) : undefined
      }
    >
      {isEditing && formData ? (
        <div className="space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="First Name" required>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                className="form-input"
                placeholder="John"
              />
            </FormField>
            <FormField label="Last Name" required>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                className="form-input"
                placeholder="Doe"
              />
            </FormField>
          </div>

          {/* Display Name */}
          <FormField
            label="Display Name"
            description={`Leave blank to use "${formData.firstName} ${formData.lastName}"`}
          >
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              className="form-input"
              placeholder={
                `${formData.firstName} ${formData.lastName}`.trim() || 'Your display name'
              }
            />
          </FormField>

          {/* Job Title */}
          <FormField label="Job Title">
            <input
              type="text"
              value={formData.jobTitle}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              className="form-input"
              placeholder="IT Manager"
            />
          </FormField>

          {/* Phone */}
          <FormField label="Phone Number">
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="form-input"
              placeholder="+1 (555) 123-4567"
            />
          </FormField>

          {/* Timezone */}
          <FormField label="Timezone" required>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              required
              className="form-input"
            >
              {commonTimezones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-3 text-sm">
            <dt className="font-medium text-zinc-500 dark:text-zinc-400">Name</dt>
            <dd className="text-zinc-900 dark:text-zinc-100">
              {profile.firstName} {profile.lastName}
            </dd>

            {profile.displayName && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Display Name</dt>
                <dd className="text-zinc-900 dark:text-zinc-100">{profile.displayName}</dd>
              </>
            )}

            <dt className="font-medium text-zinc-500 dark:text-zinc-400">Email</dt>
            <dd className="text-zinc-900 dark:text-zinc-100">{profile.email}</dd>

            {profile.jobTitle && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Job Title</dt>
                <dd className="text-zinc-900 dark:text-zinc-100">{profile.jobTitle}</dd>
              </>
            )}

            {profile.phone && (
              <>
                <dt className="font-medium text-zinc-500 dark:text-zinc-400">Phone</dt>
                <dd className="text-zinc-900 dark:text-zinc-100">{profile.phone}</dd>
              </>
            )}

            <dt className="font-medium text-zinc-500 dark:text-zinc-400">Timezone</dt>
            <dd className="text-zinc-900 dark:text-zinc-100">
              {profile.timezone?.replace(/_/g, ' ') || 'Not set'}
            </dd>
          </div>
        </div>
      )}
    </InfoPanel>
  );
}
